import { QueryKey, useMutation, useQueryClient } from 'react-query';

import { noop } from 'dpl/shared/utils';

import createFetchData from '../utils/createFetchData';

export interface UseResourceMutationOptions<_TData = any, TBody = any> {
  url: string;
  method: string;
  headers?: Record<string, string>;
  body?: TBody;
  params?: Record<string, any>;
  config?: {
    onSettled?: (data: any, error: any) => void;
    [key: string]: any;
  };
  createFetchDataConfig?: Record<string, any>;
  refetchAfter?: boolean | 'success' | 'error';
  updateStrategy?:
    | boolean
    | 'replace'
    | 'success'
    | ((prev: any, next: any) => any);
  queryKey?: QueryKey;
}

export default function useResourceMutation<TData = any, TBody = any>({
  headers,
  body,
  method,
  params,
  url,

  // OPTIONAL react-query options
  config = {},
  createFetchDataConfig = {},

  // tpt-connect options
  // refetchAfter: what conditions to trigger a refetch under (if at all)
  // falsy: don't refetch (default)
  // true: always refetch resource after mutation
  // 'error': only refetch resource if request fails
  // 'success': only refetch resource if request succeeds
  refetchAfter,
  // updateStrategy: how to handle mutation response data in the cache
  // true/'replace': replace the query cache with the response data verbatim
  // fn: run the function with the old and new values as arguments
  updateStrategy = method === 'GET' ? 'replace' : false,

  // queryKey: the exact key for the corresponding query this mutation should update
  // REQUIRED if refetchAfter or updateStrategy is provided
  queryKey
}: UseResourceMutationOptions<TData, TBody>) {
  const { onSettled = noop, ...restConfig } = config;

  const queryClient = useQueryClient();

  return useMutation(
    async (mutateArgs: Record<string, any> = {}) => {
      let awsWafToken: string | null = null;

      try {
        awsWafToken = (await window.AwsWafIntegration?.getToken()) ?? null;
      } catch (e) {
        /** noop */
      }

      return createFetchData({
        headers: { 'x-aws-waf-token': awsWafToken as string, ...headers },
        body,
        method,
        params,
        url,
        ...createFetchDataConfig,
        ...mutateArgs
      });
    },
    {
      ...restConfig,

      onSettled: (data: any, error: any) => {
        if (
          refetchAfter === true ||
          (refetchAfter === 'error' && error) ||
          (refetchAfter === 'success' && !error)
        ) {
          queryClient
            .invalidateQueries(queryKey, { exact: true })
            .then(() => onSettled(data, error));
          return;
        }

        if (
          updateStrategy === true ||
          updateStrategy === 'replace' ||
          (updateStrategy === 'success' && !error)
        ) {
          queryClient.setQueryData(queryKey as QueryKey, data);
        } else if (typeof updateStrategy === 'function') {
          queryClient.setQueryData(queryKey as QueryKey, (prevData: any) =>
            updateStrategy(prevData, data)
          );
        }

        onSettled(data, error);
      }
    }
  );
}
